/** @jsx jsx */
import {
  Button,
  ErrorBoundary,
  Grid,
  IconButton,
  isBlank,
  Modal,
  Spacer,
  TextGroupMedium,
  ToolTip,
} from "@cd2/components"
import { Fields } from "@cd2/forms"
import { H3, P } from "@cd2/text"
import { assert, IComponentProps } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ProductLocationStockDisplay } from "../../apiHooks/productLocations"
import { useBetaFeatureAccess } from "../../Components/BetaFeatureAccess"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { RemoveRowButton } from "../../Components/Buttons/RemoveRowButton"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { brandColors, themeColors } from "../../Components/EOSThemeProvider"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { ProductSelect as ExtProductSelect } from "../../Components/ProductSelect"
import { ProductSelect2 } from "../../Components/ProductSelect2"
import { QuickToolTip } from "../../Components/QuickTooltip"
import { QuickToolTipWrapper } from "../../Components/QuickToolTipWrapper"
import { TextWithNewLines } from "../../Components/TextWithNewLines"
import { icons } from "../../constants/icons"
import { productPriceLockedAttr } from "../../constants/productPriceLockedAttr"
import {
  IGroupPrice,
  IProductGroupPriceResponse,
  useProductGroupProductPrice,
} from "../../hooks/productGroupProductPrice"
import { useApi } from "../../hooks/useApi"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useOtherBranchStockModal } from "../../hooks/useOtherBranchStockModal"
import {
  ConfirmAlternateProductModal,
  useProductAlternative,
} from "../../hooks/useProductAlternative"
import { dublCorkBranchId } from "../../../shared/constants"
import { useReloadKey } from "../../hooks/useReloadKey"
import { useWatchState } from "../../hooks/useWatchState"
import { useBuildItemHooks } from "../../Pages/BuildSets/useBuildItemHooks"
import { useSaveBuild } from "../../Pages/BuildSets/useSaveBuild"
import { StockAvailableInOtherBranchesPanel } from "../../Components/PurchaseOrdersForm2"
import { BoxQuantitySwitch } from "../../Pages/OrderRequests/OrderRequestsStandaloneForm"
import {
  ProductInfoDisplayPanel,
  ProductRecommendedPriceButton,
} from "../../Pages/Stock/Products/ProductInfoDisplayPanel"
import { IRecord } from "../../types/schema"
import { convertPriceCurrency } from "../../utils/currency"
import type { IProductStock } from "../../../server/productLocations/aggregateProductStock"
import {
  getFilteredProductLocations,
  getProductLocationPrioritized,
} from "../../utils/getProductLocations"
import { getProductSuggestedPrice, IProductPrice } from "../../utils/getProductSuggestedPrice"
import { productDpdFilter } from "../../utils/productDpdFilter"
import { IBranchSettings, useSettings } from "../../utils/useSettings"
import { orderExcludedFromMarginCheck } from "../../utils/minMarginPriceBypass"
import {
  formatPrice,
  handleCheckValidMargin,
  handleCheckValidSellingPrice,
} from "../../utils/utils"
import { BarscanColumn } from "./BarscanColumn"
import { ManagePriceBandModal } from "./ManagePriceBandModal"
import {
  BARSCAN_COLUMN_WIDTH,
  calculateDiscountPercent,
  calculateDiscountPrice,
  calculateMarginFromPrice,
  changeMarginField,
  DISCOUNT_COLUMN_WIDTH,
  filterDeleted,
  filterUsedLocations,
  genItemParentId,
  getProductItemChildren,
  IItem,
  IQuoteItemRegisterOptions,
  IQuoteOrderRecord,
  IRowProps,
  itemParentUid,
  itemTypes,
  LOCATION_COLUMN_WIDTH,
  MARGIN_COLUMN_WIDTH,
  PRICE_COLUMN_WIDTH,
  PRODUCT_SELECT_COLUMN_WIDTH,
  QUANTITY_COLUMN_WIDTH,
} from "./quoteOrderUtils"

export { handleCheckValidMargin, handleCheckValidSellingPrice }

export type IItemRowSageProductUsedPriceBand = IRecord<"price_band_products"> & {
  price_bands: IRecord<"price_bands", "name">
}

export function SageProductInput({
  registerItem,
  data,
  onChange,
  quote,
  errors,
  onRemove,
  onAppendItem,
  rawFunctions,
  renderModal,
  displayAllocationColumn,
  type,
  isCustomerViewing,
  hideMarginColumn,
  hidePriceColumn,
}: IRowProps) {
  const { handleSaveBuildForCustomer, modals: saveBuildModal } = useSaveBuild(quote)
  const saveBuildModalPortal = createPortal(
    saveBuildModal,
    document.getElementById("app-scene-portal-ref")!,
  )
  const {
    defaultStockLocation,
    barScanOrders,
    warehouses,
    showDiscountOption,
    dpdProductPrefixes,
    stockWarehouses,
    useExplicitAllocations,
    _loaded,
    useQuoteOrderStandardPricing,
    directDeliveryWarehouse,
    ...settings
  } = useSettings()
  const barscanIntegrated = barScanOrders && !quote.warehouseOverride?.disable_barscan
  const shouldShowDiscount = !!(showDiscountOption || quote.hasOpportunityCertificate)
  const [noProductPrice, setNoProductPrice] = useState(false)
  const skeem = window.skeem
  const api = useApi()
  const getGroupCostPrice = useProductGroupProductPrice()

  // The row's customer pricing, shared by the effects below so mounting a row (and
  // editing its price) doesn't refetch it. Changing product fetches it fresh.
  const rowPricingRef = useRef<{ key: string; promise: Promise<IProductPrice | null> } | null>(
    null,
  )
  function fetchRowPricing(productId: string, { fresh = false } = {}) {
    const key = `${productId}:${quote.customer?.id ?? ""}`
    if (!fresh && rowPricingRef.current?.key === key) {
      return rowPricingRef.current.promise
    }
    const promise = getProductSuggestedPrice(skeem, quote, productId)
    rowPricingRef.current = { key, promise }
    promise.catch(() => {
      if (rowPricingRef.current?.promise === promise) {
        rowPricingRef.current = null
      }
    })
    return promise
  }

  // ref and not state as it isn't up to date in the validate otherwise.
  const requiredPreferredPriceRef = useRef<
    (IProductPrice & { customerPrice: number | null }) | null
  >(null)
  // Loading the required pref price for editing items
  useAsyncEffect(async () => {
    // if no product, skip
    const requiredPreferredPrice = requiredPreferredPriceRef.current
    if (!data.product?.id) {
      return
    }
    // if we've already loaded, skip, handleChangeProduct will update this so we don't need to
    // double fetch the pref price
    if (requiredPreferredPrice) {
      return
    }
    const pricing = await fetchRowPricing(data.product.id)
    const customerPrice = convertPriceCurrency(
      pricing?.customerPrice ?? null,
      currentUser.branch.euro_pound_exchange_rate,
      pricing?.currency_name,
      quote.currency_name,
    )
    console.log("PREF PRICE LOAD", pricing, customerPrice)
    if (pricing?.requiresApprovalForChange) {
      requiredPreferredPriceRef.current = { ...pricing, customerPrice }
    } else {
      requiredPreferredPriceRef.current = null
    }
  }, [data.product?.id])

  usePriceDiffAlert({ productId: data.product?.id, renderModal })

  function setWindowCustomerPrices(key: string, data: any) {
    ;(window as any).customerPrices ||= {}
    ;(window as any).customerPrices[key] = data
  }

  const { watchedItems, calculatedTotalMax } = useBuildItemHooks({ quote, data, onChange })

  // MARK: OPPCERT Pricing
  async function getPriceUpdateValues({
    product = data.product,
    discount = data.discountPercent,
  }: { discount?: number | null; product?: IItem["product"] } = {}) {
    if (!product) {
      return
    }

    const productPricingInfo = await fetchRowPricing(product.id, { fresh: true })
    console.log("GOT suggested", productPricingInfo, "for product", product.stock_code)

    const productElligableForOpportunityCertificate =
      productPricingInfo?.applyOpportunityCertificateBreakdown

    const applyOpportunityCertificateBreakdown =
      settings.canUseOpportunityCertificate &&
      quote.hasOpportunityCertificate &&
      productElligableForOpportunityCertificate

    console.log("applyOpportunityCertificateBreakdown", applyOpportunityCertificateBreakdown, {
      canUseOpportunityCertificate: settings.canUseOpportunityCertificate,
      hasOpportunityCertificate: quote.hasOpportunityCertificate,
      productElligableForOpportunityCertificate: productElligableForOpportunityCertificate,
    })

    const productCostPricePennies = calculateCostPriceFromCurrency({
      currency_name: quote.currency_name || currentUser.branch.currency_name,
      currentUser,
      product: product,
    })

    if (!applyOpportunityCertificateBreakdown || useQuoteOrderStandardPricing) {
      console.log(
        "[getPriceUpdateValues] No opportunity certificate breakdown, setting cost price to original cost price",
      )

      if (process.env.NODE_ENV === "development") {
        console.log("[getPriceUpdateValues] set price [base]", {
          cost_price_pennies: productCostPricePennies,
          markUp: calculateMarginFromPrice(productCostPricePennies ?? 0, data.price_pennies ?? 0),
          // We override the show discount option to display it with a HikOpportunity certificate
          // Set the discount on the item to null if you stop applying the opportunity certificate
          // And you don't have the setting enabled
          discountPercent: showDiscountOption && discount ? discount : null,
          opportunity_certificate_cost_discount_amount_pennies: null,
        })
      }

      return {
        cost_price_pennies: productCostPricePennies,
        markUp: calculateMarginFromPrice(productCostPricePennies ?? 0, data.price_pennies ?? 0),
        // We override the show discount option to display it with a HikOpportunity certificate
        // Set the discount on the item to null if you stop applying the opportunity certificate
        // And you don't have the setting enabled
        discountPercent: showDiscountOption && discount ? discount : null,
        opportunity_certificate_cost_discount_amount_pennies: null,
      }
    }

    const costPricePennies = calculateDiscountPrice(productCostPricePennies, discount)

    if (process.env.NODE_ENV === "development") {
      console.log("[getPriceUpdateValues] set price [list]", {
        productCostPricePennies,
        cost_price_pennies: costPricePennies,
        markUp: calculateMarginFromPrice(costPricePennies, data.price_pennies ?? 0),
        discountPercent: discount || null,
        opportunity_certificate_cost_discount_amount_pennies:
          (productCostPricePennies ?? 0) - costPricePennies,
      })
    }

    return {
      cost_price_pennies: costPricePennies,
      markUp: calculateMarginFromPrice(costPricePennies, data.price_pennies ?? 0),
      discountPercent: discount || null,
      opportunity_certificate_cost_discount_amount_pennies:
        (productCostPricePennies ?? 0) - costPricePennies,
    }
  }

  // MARK: OPPCERT Pricing
  // Debounced function to update the price values async and debounced by fetch time
  // this is debounced so it does not interrupt the user typing in the input
  const debouncedPriceUpdateRef = useRef<any>(data.discountPercent)
  const hasOpportunityCertificateUpdateRef = useRef<any>(quote.hasOpportunityCertificate)
  function updatePriceValuesFromDiscount({
    product = data.product,
    discount = data.discountPercent,
  }: { discount?: number | null; product?: IItem["product"] } = {}) {
    if (!product) {
      return
    }
    // Track previous state for discount and opp cert and block updates when they haven't changed
    // This prevents bulk updates occurring on load - but we still want to trigger updates
    // if the opp cert value is toggled.
    if (
      debouncedPriceUpdateRef.current == discount &&
      hasOpportunityCertificateUpdateRef.current == quote.hasOpportunityCertificate
    ) {
      return
    }
    debouncedPriceUpdateRef.current = discount
    hasOpportunityCertificateUpdateRef.current = quote.hasOpportunityCertificate
    getPriceUpdateValues({ product, discount }).then(result => {
      if (result && discount == debouncedPriceUpdateRef.current) {
        onChange(result)
      }
    })
  }

  useEffect(() => {
    if (!_loaded) {
      return
    }
    updatePriceValuesFromDiscount()
  }, [quote.hasOpportunityCertificate, _loaded, data.discountPercent])

  useWatchState(
    async (prev, current) => {
      if (prev.delivery_method === current.delivery_method) {
        console.log("UDPATELOCATIONS", "NO DIFF")
        return
      }
      if (!quote.delivery_method.match(/direct/gi)) {
        console.log("UDPATELOCATIONS", "NOT DD")
        return
      }
      if (!data.product) {
        console.log("UDPATELOCATIONS", "NO PROD")
        return
      }

      console.log("updating locations")
      if (!directDeliveryWarehouse) {
        console.log("UDPATELOCATIONS", "NO SETTING LOCATION")
        return
      }
      if (
        directDeliveryWarehouse &&
        data.product_location?.warehouse?.id !== directDeliveryWarehouse?.id
      ) {
        const product = await skeem.fetch2<
          IRecord<"products", "is_consignment"> & {
            product_locations: IRecord<
              "product_locations",
              "name" | "free_stock" | "warehouse" | "specific_location"
            >[]
          }
        >({
          first: {
            name: "products",
            matches: { id: data.product.id },
            attributes: [
              "is_consignment",
              {
                name: "product_locations",
                attributes: ["name", "free_stock", "warehouse", "specific_location"],
              },
            ],
          },
        })

        const location = getProductLocationPrioritized(
          getFilteredProductLocations(product.product_locations),
          {
            defaultStockLocation: currentUser?.defaultWarehouse?.name ?? defaultStockLocation,
            branch: currentUser.branch,
            warehouses: [],
            defaultIntercoWarehouse: settings.defaultIntercoWarehouse,
            directDelivery: true,
            directDeliveryWarehouse: directDeliveryWarehouse,
          },
          {
            isConsignment: product.is_consignment,
            isInterco: quote.customer?.internalCustomer,
            override: quote.warehouseOverride,
          },
        )
        console.log("matched", location)
        if (location?.id !== data.product_location?.id) {
          onChange({ product_location: location })
        }
      }
    },
    {
      internal_branch: quote.internal_transfer_to_branch?.id,
      delivery_method: quote.delivery_method,
    },
  )

  async function confirmAlternativeProduct(data: any) {
    const result = await renderModal({
      Component: ({ onResolve }) => (
        <ConfirmAlternateProductModal data={data} onResolve={onResolve} />
      ),
    })
    return result as { id: string }
  }

  const checkProductForAlternative = useProductAlternative()

  // get childItems for this sage product row
  const childItems = getProductItemChildren(quote, data)
  // Null when loading
  const [usingNonStandardPrice, setUsingNonStandardPrice] = useState<null | boolean>(null)
  const { currentUser } = useGlobalState()
  const hasBetaFeatureAccess = useBetaFeatureAccess()

  const [selectedProductPriceLocked, setSelectedProductPriceLocked] = useState(true)

  // The minimum selling price and minimum margin for the selected product.
  // These come from the product OR, where the product does not set them, its
  // product group. Product level minimums supercede the product group ones.
  const [productMinimums, setProductMinimums] = useState<{
    minimumSellingPricePennies: number | null
    minimumMargin: number | null
  } | null>(null)

  useAsyncEffect(async () => {
    if (!data.product?.id) {
      setProductMinimums(null)
      return
    }
    const product = await skeem.fetch2<
      IRecord<"products", "minimum_margin" | "minimum_selling_price_pennies"> & {
        product_group?: IRecord<"product_groups", "minimum_product_margin">
      }
    >({
      first: {
        name: "products",
        attributes: [
          "minimum_margin",
          "minimum_selling_price_pennies",
          { name: "product_group", attributes: ["minimum_product_margin"] },
        ],
        matches: { id: data.product.id },
      },
    })
    setProductMinimums({
      minimumSellingPricePennies: product.minimum_selling_price_pennies ?? null,
      minimumMargin: product.minimum_margin ?? product.product_group?.minimum_product_margin ?? null,
    })
  }, [data.product?.id])

  useAsyncEffect(async () => {
    if (data.product?.id) {
      const product = await skeem.fetch2<{ id: string; priceLocked: boolean }>({
        first: {
          name: "products",
          attributes: [productPriceLockedAttr.product],
          filter: { matches: { id: data.product.id } },
        },
      })
      setSelectedProductPriceLocked(!!product?.priceLocked)
    } else {
      setSelectedProductPriceLocked(false)
    }
  }, [data.product?.id])

  const [focused, setFocused] = useState(false)
  const [stockData, setStockData] = useState<IProductStock | null>(null)
  const freeStockForItem = stockData
    ? stockData.inStock -
      stockData.allocatedStock +
      (stockData.allocatedToOrders.find(o => o.id === quote.id)?.quantity ?? 0)
    : 0

  const [isUsingGroupPrice, setIsUsingGroupPrice] = useState<null | IProductGroupPriceResponse>(
    null,
  )
  const [hasAlternateProduct, setHasAlternateProduct] = useState(false)
  const [selectedProductPriceLockedDueToSupplier, setSelectedProductPriceLockedDueToSupplier] =
    useState(false)
  const [customBody, setCustomBody] = useState(
    data.product && !isBlank(data.body) && data.body !== data.product?.body,
  )
  const [productComponentReloadKey, reloadProductSelectComponent] = useReloadKey()

  const canEditPricing =
    !!currentUser.canUseNonPriceBandPricesOnQuotesOrders || currentUser.isSuperAdmin

  async function loadPriceBandData() {
    if (!data.product?.id || !quote.customer?.id) {
      return
    }
    const pricing = await fetchRowPricing(data.product.id)

    const convertedPrice = convertPriceCurrency(
      pricing?.customerPrice ?? null,
      currentUser.branch.euro_pound_exchange_rate,
      pricing?.currency_name,
      quote.currency_name,
    )

    if (pricing && data.price_pennies === convertedPrice) {
      setUsingNonStandardPrice(pricing.usingNonStandardPrice)
      onChange({
        price_locked: pricing.usingNonStandardPrice && !canEditPricing,
      })
      return
    }

    setUsingNonStandardPrice(false)
    onChange({
      price_locked: false,
    })
  }

  useAsyncEffect(async () => {
    if (!_loaded) {
      return
    }
    if (data.product?.id) {
      if (data.product_location?.id) {
        const stockData = await api.locationsStock([data.product_location.id], {
          allocationAttribute: useExplicitAllocations ? "allocated_quantity" : "quantity",
        })
        setStockData(stockData[data.product_location.id])
      }

      const groupPrice = await getGroupCostPrice(data.product.id)
      if (groupPrice?.cost_price_pennies) {
        setIsUsingGroupPrice(groupPrice)
      } else {
        setIsUsingGroupPrice(null)
      }
    }
  }, [data.product_location?.id, data.product?.id, stockWarehouses, useExplicitAllocations, _loaded])

  useAsyncEffect(async () => {
    if (useQuoteOrderStandardPricing && data.standard_price_pennies) {
      rawFunctions.onChangeSilent({
        standard_discount_percent: calculateDiscountPercent(
          data.standard_price_pennies,
          data.price_pennies,
        ),
      })
    } else if (useQuoteOrderStandardPricing) {
      if (data.product) {
        const pricing = await fetchRowPricing(data.product.id)
        const convertedPrice = convertPriceCurrency(
          pricing?.customerPrice ?? null,
          currentUser.branch.euro_pound_exchange_rate,
          pricing?.currency_name,
          quote.currency_name || currentUser.branch.currency_name,
        )
        onChange({
          standard_price_pennies: convertedPrice,
          standard_discount_percent: calculateDiscountPercent(convertedPrice, data.price_pennies),
        })
      }
    }
  }, [useQuoteOrderStandardPricing])

  useAsyncEffect(async () => {
    await loadPriceBandData()
  }, [data.price_pennies, data.product?.id, quote.customer?.id, quote.currency_name])

  useAsyncEffect(async () => {
    if (!data.product?.id) {
      return
    }
    const product = await skeem.fetch2<{
      preferred_supplier: IRecord<"suppliers", "disable_price_editing">
    }>({
      first: {
        name: "products",
        filter: { matches: { id: data.product.id } },
        attributes: [{ name: "preferred_supplier", attributes: ["disable_price_editing"] }],
      },
    })
    console.log(product.preferred_supplier)
    if (product.preferred_supplier?.disable_price_editing) {
      setSelectedProductPriceLockedDueToSupplier(true)
    }
  }, [data.product?.id])

  function isEmptyItem(item: IItem) {
    return !item.price_pennies && !item.product?.id
  }

  function shouldKeepItem(item: IItem): item is IItem & {
    product: Exclude<IItem["product"], undefined>
    product_location: Exclude<IItem["product_location"], undefined>
  } {
    return !isEmptyItem(item) && !!item.product && !!item.product_location
  }

  function getRegistrationOptions(): IQuoteItemRegisterOptions {
    return {
      validate(item, savingQuote) {
        const errors = {} as any
        const warnings = []

        const marginCheck = handleCheckValidMargin(
          item.cost_price_pennies,
          item.price_pennies,
          item.product?.minimum_margin ?? item.product?.product_group?.minimum_product_margin,
          item.stock_code,
        )

        if (marginCheck && !orderExcludedFromMarginCheck({ customer: savingQuote.customer })) {
          warnings.push(`${item.stock_code} is below the minimum margin of ${marginCheck.margin}%`)
        }

        if (
          requiredPreferredPriceRef.current?.requiresApprovalForChange &&
          requiredPreferredPriceRef.current.customerPrice &&
          requiredPreferredPriceRef.current.customerPrice !== item.price_pennies
        ) {
          warnings.push(
            `${item.stock_code} matched the price band ${requiredPreferredPriceRef.current.sourceName} and requires approval to be changed`,
          )
        }

        if (isEmptyItem(item)) {
          return { warnings: ["A sage row has not been filled in and will be removed"] }
        } else {
          if (!item.product) {
            errors.product = "Required"
          }

          const itemChildren = getProductItemChildren(savingQuote, item)

          if (
            itemChildren.filter(
              quoteItem =>
                item.product_location &&
                quoteItem.product_location &&
                quoteItem.type === itemTypes.sage &&
                quoteItem.product_location.id === item.product_location.id,
            ).length > 1
          ) {
            warnings.push("There are multiple lines with the same location")
          }

          if (!item.quantity) {
            errors.quantity = "Required"
          }

          if ((item.quantity ?? 0) < (item.allocated_quantity ?? 0)) {
            errors.allocated_quantity = "Cannot allocate more than quantity"
          }

          if (
            !item.price_pennies &&
            !settings.allowZeroPriceQuotesOrders &&
            !quote.isInternalTransfer
          ) {
            errors.price_pennies = "Required"
          }

          if (item.price_pennies && item.cost_price_pennies) {
            if (item.cost_price_pennies > item.price_pennies) {
              warnings.push("A sage product has a cost price higher then what you have quoted")
            }
          }

          if ((item?.price_pennies || 0) < 0) {
            errors.price_pennies = "Cannot be negative"
          }

          const sellingPriceCheck = handleCheckValidSellingPrice(
            item.price_pennies,
            item.product?.minimum_selling_price_pennies,
            item.stock_code,
          )
          if (sellingPriceCheck) {
            warnings.push(
              `${item.stock_code} is below the minimum selling price of ${sellingPriceCheck.formatted}`,
            )
          }

          if (item.product && item.product.branch.id !== currentUser.branch.id) {
            errors.product = "Must be exported to Sage"
          }
        }

        return { errors, warnings }
      },
      onSave(item) {
        if (shouldKeepItem(item)) {
          if (item.supplierId) {
            delete item.supplierId
          }
          return {
            ...item,
            markUp: Number(item.markUp || 0),
            quantity: Number(item.quantity || 1),
            product: item.product,
            product_location: item.product_location,
          }
        } else {
          return { id: item.id, $deleted: true }
        }
      },
    }
  }

  useEffect(() => {
    if (!_loaded) {
      return
    }
    registerItem(getRegistrationOptions())
  }, [_loaded])

  // map over childItems, setting their parentId to this component records and registering them.
  function handleRegisterChildren() {
    for (const item of childItems) {
      if (!rawFunctions.registeredItems[item.id]) {
        rawFunctions.handleItemChange(item.id, old => ({ ...old }))
        rawFunctions.registerItem(item, getRegistrationOptions())
      }
    }
  }

  // Watch for parent item changes, mirror those into the childItems
  useEffect(() => {
    const {
      id,
      product_location,
      quantity,
      allocated_quantity,
      product,
      parent_uid,
      specialItem,
      notes,
      ...changes
    } = data
    rawFunctions.batchItemChange(childItems, data => ({ ...data, ...changes }))
  }, [JSON.stringify({ ...data, standard_discount_percent: undefined })])

  // Register the child items for validations and saving when childItems are added.
  // Watching the childItem ids, so that after save will re-register child items
  useEffect(() => {
    handleRegisterChildren()
  }, [JSON.stringify(childItems.sort((a, b) => a.id.localeCompare(b.id)).map(v => v.id))])

  // Set the initial markup, only react onload and set the changes to markup in the price
  // on change methods to prevent rendering markup input unintentionally
  useEffect(() => {
    onChange({
      markUp: calculateMarginFromPrice(
        data.cost_price_pennies || undefined,
        data.price_pennies || undefined,
      ),
    })
  }, [])

  const cost_price_pennies = data.cost_price_pennies || 0

  function handleQuotedPriceChange(changedData: {
    price: number | null
    discountPercent?: number | null
  }) {
    if (settings.displayQuotesWithSellingPriceAndDiscounts) {
      onChange({
        price_pennies: changedData.price,
        markUp: calculateMarginFromPrice(
          data.cost_price_pennies || undefined,
          changedData.price ?? 0,
        ),
        fixed_discount_rate: changedData.discountPercent,
      })
    } else {
      onChange({
        price_pennies: changedData.price,
        markUp: calculateMarginFromPrice(
          data.cost_price_pennies || undefined,
          changedData.price ?? 0,
        ),
      })
    }
  }

  function handleStandardPriceChange({
    price,
    discountPercent,
  }: {
    price?: number | null
    discountPercent?: number | null
  }) {
    const item = {
      price_pennies: price,
      standard_discount_percent: calculateDiscountPercent(data.standard_price_pennies, price),
      markUp: calculateMarginFromPrice(data.cost_price_pennies || undefined, price ?? 0),
    } as any
    if (settings.displayQuotesWithSellingPriceAndDiscounts) {
      item.fixed_discount_rate = discountPercent
    }

    onChange(item)
  }

  function handleStandardDiscountChange(discountPct?: number | null) {
    const calculatedPrice = calculateDiscountPrice(data.standard_price_pennies, discountPct)
    const item = {
      price_pennies: calculatedPrice,
      standard_discount_percent: discountPct,
      markUp: calculateMarginFromPrice(data.cost_price_pennies || undefined, calculatedPrice),
    } as any

    onChange(item)
  }

  function handleMarkupChange(markUp: number | null) {
    if (markUp) {
      const price_pennies = Number(
        ((cost_price_pennies || 0) / ((100 - (Number(markUp) || 0)) / 100)).toFixed(0),
      )
      onChange({
        price_pennies,
        markUp,
      })
    } else {
      const price_pennies = data.cost_price_pennies
      onChange({ price_pennies, markUp: markUp || undefined })
    }
  }

  async function handleProductChange(pid: string) {
    let productId = pid
    const altProduct = await checkProductForAlternative(productId)
    if (altProduct.alternative) {
      const selected = await confirmAlternativeProduct(altProduct)
      productId = selected.id
    }
    const dpdProductFilter = productDpdFilter(dpdProductPrefixes ?? "")

    if (!productId) {
      await skeem.mutate({
        alerts: {
          create: {
            type: "SageProductInput",
            body: `No product ID selected`,
            details: { pid, altProduct: altProduct },
            notifications: ["support@cd2solutions.co.uk", "henry@cd2solutions.co.uk"],
          },
        },
      })
    }
    assert(productId, "useBuildSetBuilder: No product ID selected")

    type IProduct = IRecord<
      "products",
      | "stock_code"
      | "body"
      | "category"
      | "cost_price_pennies"
      | "line_item_description"
      | "sale_price_pennies"
      | "memo"
      | "price_updated_at"
      | "sage_id"
      | "is_consignment"
      | "original_cost_price_pennies"
      | "minimum_selling_price_pennies"
      | "minimum_margin"
      | "preferred_supplier_list_price_pennies"
      | "active"
    > & {
      branch: IRecord<"branches", "name" | "consignmentWarehouse" | "currency_name">
      product_locations: IRecord<
        "product_locations",
        "name" | "free_stock" | "warehouse" | "specific_location"
      >[]
      suppliers: IRecord<"suppliers", "currency_name">[]
      preferred_supplier: IRecord<"suppliers", "currency_name">
      product_group: IRecord<
        "product_groups",
        | "products_price_method"
        | "memo"
        | "description"
        | "code"
        | "minimum_product_margin"
        | "requires_elevated_min_price_approval"
      >
    }
    const productFetch = skeem.fetchOne<IProduct>({
      products: {
        attributes: [
          "preferred_supplier_list_price_pennies",
          "stock_code",
          "body",
          "category",
          "cost_price_pennies",
          "line_item_description",
          "sale_price_pennies",
          "memo",
          "price_updated_at",
          "sage_id",
          "is_consignment",
          "original_cost_price_pennies",
          "minimum_margin",
          "minimum_selling_price_pennies",
          "active",
          { name: "branch", attributes: ["name", "consignmentWarehouse", "currency_name"] },
          { name: "suppliers", attributes: ["currency_name"] },
          { name: "preferred_supplier", attributes: ["currency_name"] },
          {
            name: "product_group",
            attributes: [
              "products_price_method",
              "memo",
              "description",
              "code",
              "minimum_product_margin",
              "requires_elevated_min_price_approval",
            ],
          },
          {
            name: "product_locations",
            attributes: ["name", "free_stock", "warehouse", "specific_location"],
          },
        ],
        filter: {
          and: [
            { matches: { id: productId } },
            barScanOrders && dpdProductFilter.and.length > 0 && dpdProductFilter,
          ].filter(Boolean),
        },
      },
    })

    let customerPrice = undefined
    let customerPriceDiscountPercent = undefined

    const pricing = await fetchRowPricing(productId, { fresh: true })
    console.log("load OG COST", pricing)
    customerPrice = pricing?.customerPrice
    customerPriceDiscountPercent = pricing?.customerPriceDiscountPercent
    customerPrice = convertPriceCurrency(
      customerPrice ?? null,
      currentUser.branch.euro_pound_exchange_rate,
      pricing?.currency_name,
      quote.currency_name,
    )
    if (pricing?.requiresApprovalForChange) {
      requiredPreferredPriceRef.current = { ...pricing, customerPrice }
    } else {
      requiredPreferredPriceRef.current = null
    }

    if (pricing?.usingNonStandardPrice) {
      setUsingNonStandardPrice(true)
    }

    const product = await productFetch

    const locations = product.product_locations

    const childItems = getProductItemChildren(quote, {
      ...data,
      product,
    })

    const productLocation = getProductLocationPrioritized(
      filterUsedLocations(getFilteredProductLocations(locations), { items: childItems }, data.id),
      {
        defaultStockLocation: currentUser?.defaultWarehouse?.name ?? defaultStockLocation,
        branch: currentUser.branch,
        warehouses,
        defaultIntercoWarehouse: settings.defaultIntercoWarehouse,
        directDelivery: !!quote.delivery_method?.match(/direct/i),
        directDeliveryWarehouse: directDeliveryWarehouse,
      },
      {
        isConsignment: product.is_consignment,
        isInterco: quote.customer?.internalCustomer,
        override: quote.warehouseOverride,
      },
    )

    const useBody = !product.line_item_description
    setCustomBody(!useBody)

    // Refetching child items to ensure the current state here is up to date
    const childItemsBeforeChange = getProductItemChildren(quote, data)

    // Remove any child items that belong to this item
    childItemsBeforeChange.map(child => rawFunctions.handleRemoveItem(child))

    const standardPriceCurrencyConverted = convertPriceCurrency(
      pricing?.standardPrice ?? null,
      currentUser.branch.euro_pound_exchange_rate,
      pricing?.currency_name,
      quote.currency_name,
    )

    const priceUpdateValues = await getPriceUpdateValues({
      discount: data.standard_discount_percent,
      product,
    })
    console.log("priceUpdateValues", priceUpdateValues)
    const costPricePenniesUpdated = priceUpdateValues?.cost_price_pennies

    const itemData = {
      product,
      product_location: productLocation,
      stock_code: product.stock_code,
      stock_location: productLocation?.name ?? "",
      body: useBody ? product.body : product.line_item_description,
      category: product.category,
      cost_price_pennies: costPricePenniesUpdated,
      price_pennies: customerPrice || null,
      markUp: calculateMarginFromPrice(
        costPricePenniesUpdated || undefined,
        customerPrice ?? undefined,
      ),
      parent_uid: data.parent_uid || genItemParentId("parent"),
      fixed_discount_rate: customerPriceDiscountPercent,
      standard_price_pennies: useQuoteOrderStandardPricing
        ? standardPriceCurrencyConverted
        : undefined,
      standard_discount_percent: useQuoteOrderStandardPricing
        ? calculateDiscountPercent(standardPriceCurrencyConverted, customerPrice)
        : undefined,
    }
    setWindowCustomerPrices(data.id, pricing)
    if (product.product_group?.memo) {
      await alertProductMemo(
        {
          name: `${product.body}'s product group ${product.product_group?.code}`,
          memo: product.product_group?.memo,
        },
        "product group",
      )
    }
    if (product.memo) {
      await alertProductMemo({ name: product.body, memo: product.memo }, "product")
    }
    onChange(itemData)
    setNoProductPrice(!itemData.price_pennies && !itemData.standard_price_pennies)
  }

  // Loads product location with free_stock, assigns product location to specified item
  // This is using the raw function to work with the childItems as well as the main item.
  async function handleProductLocationChange(item: IItem, locationId: string) {
    const location = await skeem.fetchOne<Exclude<IItem["product_location"], undefined>>({
      product_locations: {
        attributes: ["name", "free_stock", "warehouse"],
        filter: { matches: { id: locationId } },
      },
    })

    rawFunctions.handleItemChange(item.id, oldItem => ({
      ...oldItem,
      product_location: location,
      stock_location: location.name,
    }))
  }

  let markUp

  if (typeof data.markUp === "number") {
    markUp = data.markUp
  } else {
    // intial load
    markUp = calculateMarginFromPrice(
      data.cost_price_pennies || undefined,
      data.price_pennies || undefined,
    )
  }

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const alertProductMemo = useAsyncCallback(
    async (data: { name: string; memo: string }, type: "product" | "product group") =>
      await renderModal({
        Component: ({ onResolve }) => (
          <Modal
            isOpen={true}
            onRequestClose={() => onResolve(null)}
            css={css`
              max-width: calc(100% - 16px);
              overflow: initial;
              text-align: left;
            `}
            render={() => (
              <React.Fragment>
                <TextGroupMedium
                  heading={`Memo for ${data.name}`}
                  body={`This memo has been added to the ${type}'s record. Change on the ${type} edit page.`}
                  theme={{ heading: { size: "small" } }}
                />
                <Spacer />
                <div
                  css={css`
                    border-radius: 8px;
                    background-color: #f5f5f5;
                    padding: 16px;
                  `}
                >
                  <TextWithNewLines text={data.memo} />
                </div>
                <Spacer />
                <div>
                  <Button onClick={() => onResolve(null)} buttonColor="white">
                    Okay
                  </Button>
                </div>
              </React.Fragment>
            )}
          />
        ),
      }),
  )

  const handleChangeSpecialItemNotes = useAsyncCallback(async () =>
    renderModal({
      Component: ({ onResolve }) => {
        const [value, setValue] = useState(data.notes ?? "")
        const [isSpecialItem, setIsSpecialItem] = useState(true)
        const ref = useRef<HTMLTextAreaElement | null>(null)
        useEffect(() => {
          ref.current?.focus()
        }, [ref.current])
        return (
          <Modal
            render={() => (
              <div>
                <Grid gap={8}>
                  <TextGroupMedium heading="Edit notes for this special item" />
                  <Fields.TextArea
                    ref={ref}
                    name="notesField"
                    onChange={setValue}
                    value={value}
                    inputProps={{ style: { height: 74 } }}
                    placeholder={
                      "These notes will be displayed above the pricing data when this product and customer combination is selected again"
                    }
                  />
                  <Fields.Checkbox
                    label="Is Special Item"
                    onChange={setIsSpecialItem}
                    value={isSpecialItem}
                  />
                  <ConfirmCancelButtons
                    onConfirm={() => {
                      onChange({ notes: isSpecialItem ? value : "", specialItem: isSpecialItem })
                      return onResolve(true)
                    }}
                    onCancel={() => {
                      setValue(data.notes ?? "")
                      setIsSpecialItem(data.specialItem ?? false)
                      return onResolve(false)
                    }}
                  />
                </Grid>
              </div>
            )}
            isOpen
          />
        )
      },
    }),
  )

  const handleManagePriceBands = useAsyncCallback(async () =>
    renderModal({
      Component: ({ onResolve }) => (
        <Modal
          onRequestClose={() => onResolve(false)}
          render={() => (
            <ManagePriceBandModal
              customerId={quote.customer?.id}
              productId={data.product?.id}
              onResolve={onResolve}
              newPrice={data.price_pennies}
            />
          )}
          isOpen
        />
      ),
    }),
  )

  function handleCostPricePenniesChange(price: number) {
    onChange({
      cost_price_pennies: price,
      markUp: calculateMarginFromPrice(price, data.price_pennies || undefined),
    })
  }

  function handleStandardCostPricePenniesChange(price: number) {
    onChange({
      standard_price_pennies: price,
      discountPercent: calculateDiscountPercent(price, data.price_pennies || undefined),
    })
  }

  function handleDiscountChange(val: number | null) {
    // set the value  as null so it doesn't flash as 0 when clearing the field
    // the prices will be updated by the useEffect calling asyncSetPriceUpdateValues
    onChange({ discountPercent: val ? val : null })
  }

  const [rawMargin, setRawMargin] = useState(typeof markUp === "number" ? markUp : null)
  useEffect(() => setRawMargin(data.markUp), [data.markUp])

  const locationsToSplitStock =
    data && quote
      ? filterUsedLocations(
          getFilteredProductLocations(data.product?.product_locations ?? [], warehouses),
          { items: childItems },
          data.id,
        )
      : undefined

  const hasLocationsToSplitStock =
    data && quote && locationsToSplitStock && locationsToSplitStock.length > 1

  const canSplitStock =
    onAppendItem &&
    hasLocationsToSplitStock &&
    !!locationsToSplitStock?.some(location => (location.free_stock ?? 0) > 0)

  return (
    <React.Fragment>
      {saveBuildModalPortal}
      <div
        onFocus={() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
          setFocused(true)
        }}
        onBlur={() => (timeoutRef.current = setTimeout(() => setFocused(false), 400))}
        style={{ outline: "none" }}
        tabIndex={-1}
      >
        <Grid
          columns={
            [
              PRODUCT_SELECT_COLUMN_WIDTH,
              barscanIntegrated && !quote.isInternalTransfer && BARSCAN_COLUMN_WIDTH,
              LOCATION_COLUMN_WIDTH,
              QUANTITY_COLUMN_WIDTH,
              useExplicitAllocations && QUANTITY_COLUMN_WIDTH,
              shouldShowDiscount && DISCOUNT_COLUMN_WIDTH,
              !isCustomerViewing && PRICE_COLUMN_WIDTH,
              !hidePriceColumn && !quote.isInternalTransfer && PRICE_COLUMN_WIDTH,
              hideMarginColumn || quote.isInternalTransfer
                ? null
                : useQuoteOrderStandardPricing || !isCustomerViewing
                ? MARGIN_COLUMN_WIDTH
                : null,
            ].filter(Boolean) as any
          }
          gap={6}
        >
          <Grid
            columns={
              customBody ? [{ fr: 1 }, { fr: 1.2 }, "max-content"] : ["auto", "auto", "max-content"]
            }
            gap={8}
          >
            <Grid.Area column={[1, 2]}>
              <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={4}>
                <Grid.Area column={customBody ? 1 : [1, 2]}>
                  <ErrorBoundary
                    key={productComponentReloadKey}
                    ErrorComponent={() => (
                      <div>
                        An error has occurred with this component.{" "}
                        <Button onClick={reloadProductSelectComponent} buttonStyle="text">
                          Click here to refresh the component
                        </Button>{" "}
                      </div>
                    )}
                  >
                    <ProductSelect2
                      value={data.product}
                      onChange={product => handleProductChange(product.id)}
                      error={errors.product}
                      autoFocus={!data.product}
                      preventSelectUnexported
                      customerId={quote.customer?.id}
                    />
                  </ErrorBoundary>
                  <BoxQuantitySwitch
                    productId={data.product?.id}
                    itemQuantity={data.quantity}
                    onChange={quantity =>
                      data.type === itemTypes.buildSetItem
                        ? onChange({ build_quantity: quantity })
                        : onChange({ quantity })
                    }
                  />
                  <StockAvailableInOtherBranchesPanel product={data.product} />
                </Grid.Area>
                {customBody && (
                  <Grid.Area column={2}>
                    <Fields.TextArea
                      onChange={value => onChange({ body: value })}
                      value={data.body}
                      inputProps={{ style: { height: 40 } }}
                      placeholder={data.product?.body ?? "Enter a description..."}
                    />
                  </Grid.Area>
                )}
                <AlternateProductLoader
                  product={data.product}
                  setHasAlternateProduct={setHasAlternateProduct}
                  handleSwitchToAlternateProduct={id => handleProductChange(id)}
                />
                {type === "quote" && (
                  <Grid.Area column={[1, 2]}>
                    <Grid gap={8}>
                      {settings.useQuoteSpecialItems && (
                        <Fields.Checkbox
                          label="This is a special order"
                          value={!!data.special_order_item}
                          onChange={value => {
                            onChange({ special_order_item: value })
                          }}
                        />
                      )}
                      <div
                        css={css`
                          display: inline-block;
                          width: max-content;
                        `}
                      >
                        <Fields.Checkbox
                          label="Optional"
                          onChange={() =>
                            onChange({ is_rich_quote_optional: !data.is_rich_quote_optional })
                          }
                          value={!!data.is_rich_quote_optional}
                        />
                      </div>
                    </Grid>
                  </Grid.Area>
                )}
                {isUsingGroupPrice && isUsingGroupPrice.branch?.id !== currentUser.branch.id && (
                  <Grid.Area column={[1, 2]}>
                    <Grid
                      gap={4}
                      css={css`
                        padding: 8px;
                        background-color: #f5f5f5;
                        border-radius: 8px;
                      `}
                    >
                      {isUsingGroupPrice && (
                        <P size="small" weight={600}>
                          The cost price and description for this item are based from values from
                          the following branch: {isUsingGroupPrice.branch?.name}.
                        </P>
                      )}
                    </Grid>
                  </Grid.Area>
                )}
              </Grid>
            </Grid.Area>
            <Grid.Area column={3}>
              <QuickToolTip
                content={customBody ? "Revert to product description" : "Edit the description"}
              >
                <IconButton
                  icon={customBody ? icons.cross : icons.edit}
                  iconSize={14}
                  buttonStyle="text"
                  onClick={() =>
                    setCustomBody(currentUseCustomerBody => {
                      if (currentUseCustomerBody) {
                        // if old is true, new value will be false.
                        onChange({ body: data.product?.body ?? "" })
                      }
                      return !currentUseCustomerBody
                    })
                  }
                  square
                />
              </QuickToolTip>
            </Grid.Area>
          </Grid>
          {barscanIntegrated && !quote.isInternalTransfer && (
            <div>
              <BarscanColumn onChange={onChange} values={data} />
            </div>
          )}
          <div>
            <ProductLocationWithStock
              onChange={handleProductLocationChange}
              data={data}
              childItems={childItems}
              warehouses={warehouses}
              warehouseOverride={quote.warehouseOverride}
            />
            {data.type !== itemTypes.buildSet &&
              focused &&
              !!data.product?.id &&
              !!data.product_location?.id && (
                <React.Fragment>
                  <Spacer />
                  <StockDisplay
                    productLocationId={data.product_location.id}
                    productId={data.product.id}
                    quoteId={quote.id}
                    quantityAmount={data.quantity ?? 0}
                    onAppendItem={
                      data.type === itemTypes.buildSetItem || quote.warehouseOverride
                        ? undefined
                        : onAppendItem
                        ? quantityChanges => {
                            onChange({
                              quantity: quantityChanges.updateQuantity,
                              allocated_quantity: quantityChanges.updateQuantity,
                            })
                            onAppendItem({
                              ...data,
                              quantity: quantityChanges.newItemQuantity,
                              allocated_quantity: quantityChanges.newItemQuantity,
                              product_location: quantityChanges.newProductLocation,
                            })
                          }
                        : undefined
                    }
                    showAllBranchesLink
                  />
                </React.Fragment>
              )}
          </div>
          <div>
            <Fields.Number
              placeholder="99"
              value={data.quantity || data.quantity === 0 ? String(data.quantity) : null}
              onChange={val =>
                displayAllocationColumn
                  ? onChange({ quantity: Number(val) })
                  : onChange({
                      quantity: Number(val),
                      allocated_quantity: Number(val),
                    })
              }
              inputProps={{ min: 1 }}
              error={errors.quantity}
            />
            {data.type == itemTypes.buildSet && (
              <span
                css={css`
                  text-decoration: underline;
                  font-size: 12px;
                  display: inline-block;
                  color: #666;
                `}
              >
                Max build: {calculatedTotalMax}
              </span>
            )}
            {useExplicitAllocations && stockData?.inStock !== undefined && (
              <QuickToolTip content="The allocated amount will not exceed this total. Saving with more items will only partially allocate this order">
                <span
                  css={css`
                    font-size: 12px;
                    font-style: italic;
                    color: ${(data.quantity ?? 0) > freeStockForItem ? "red" : "grey"};
                  `}
                >
                  {freeStockForItem} free stock
                </span>
              </QuickToolTip>
            )}
          </div>
          {useExplicitAllocations && (
            <Grid columns={["auto", "max-content"]} gap={4}>
              <Fields.Number
                placeholder="99"
                value={
                  data.allocated_quantity || data.allocated_quantity === 0
                    ? String(data.allocated_quantity)
                    : null
                }
                onChange={val => onChange({ allocated_quantity: Number(val) })}
                inputProps={{ min: 1 }}
                disabled={!displayAllocationColumn}
                error={errors.allocated_quantity}
              />
              {(data.quantity ?? 0) > (data.product_location?.free_stock ?? 0) && (
                <ToolTip
                  hoverContent={() => (
                    <React.Fragment>
                      <P>More stock than is available has been allocated.</P>
                      <P>
                        Allocated quantity will be adjusted to the max available free stock on save.
                      </P>
                    </React.Fragment>
                  )}
                >
                  <P size="large" blockSpacing={0}>
                    *
                  </P>
                </ToolTip>
              )}
            </Grid>
          )}
          {shouldShowDiscount && (
            <Fields.Number
              prefix="%"
              placeholder="10"
              value={
                data.discountPercent || data.discountPercent === 0
                  ? String(data.discountPercent)
                  : null
              }
              onChange={val => handleDiscountChange(Number(val))}
              inputProps={{ min: 0 }}
              error={errors.discountPercent}
              disabled={!showDiscountOption && quote.hasOpportunityCertificate}
            />
          )}
          {!isCustomerViewing && (
            <div>
              {data.product &&
              (useQuoteOrderStandardPricing
                ? data.standard_price_pennies
                : data.cost_price_pennies) ? (
                <Fields.Price
                  placeholder="0.00"
                  value={
                    useQuoteOrderStandardPricing
                      ? data.standard_price_pennies ?? 0
                      : data.cost_price_pennies
                  }
                  prefix={false}
                  onChange={
                    useQuoteOrderStandardPricing
                      ? handleStandardCostPricePenniesChange
                      : handleCostPricePenniesChange
                  }
                  disabled
                  inPennies
                />
              ) : (
                <Fields.Number placeholder="Unavailable" value={null} disabled />
              )}
            </div>
          )}

          {!hidePriceColumn && !quote.isInternalTransfer && (
            <div>
              <QuickToolTipWrapper
                content={
                  <P color="white">
                    {selectedProductPriceLockedDueToSupplier
                      ? "Products from this supplier cannot have their prices edited"
                      : "This price comes from a specific price band and cannot be edited"}
                  </P>
                }
                showToolTip={
                  (usingNonStandardPrice === true && !canEditPricing) ||
                  selectedProductPriceLockedDueToSupplier
                }
              >
                <Fields.Price
                  key={`${data.product?.id}`}
                  disabled={
                    selectedProductPriceLockedDueToSupplier ||
                    ((usingNonStandardPrice || usingNonStandardPrice === null) && !canEditPricing)
                  }
                  css={
                    usingNonStandardPrice
                      ? css`
                          background-color: ${themeColors.lightGreen};
                        `
                      : undefined
                  }
                  placeholder="123.45"
                  value={data.price_pennies}
                  onChange={price =>
                    useQuoteOrderStandardPricing
                      ? handleStandardPriceChange({
                          price,
                          discountPercent: null,
                        })
                      : handleQuotedPriceChange({
                          price,
                          discountPercent: null,
                        })
                  }
                  error={errors.price_pennies}
                  prefix={false}
                  inPennies
                />
              </QuickToolTipWrapper>
              {(productMinimums?.minimumSellingPricePennies != null ||
                productMinimums?.minimumMargin != null) && (
                <div
                  css={css`
                    font-size: 12px;
                    color: #666;
                  `}
                >
                  {productMinimums.minimumSellingPricePennies != null && productMinimums.minimumSellingPricePennies > 0 && (
                    <div
                      css={css`
                        font-size: 15px;
                        font-weight: 700;
                        color: #8b0000;
                      `}
                    >
                      Min price:{" "}
                      {formatPrice(productMinimums.minimumSellingPricePennies, { isPennies: true })}
                    </div>
                  )}
                  {productMinimums.minimumMargin != null && (
                    <div>Min margin: {productMinimums.minimumMargin}%</div>
                  )}
                </div>
              )}
              <QuickToolTip
                content={
                  data.specialItem
                    ? "This item has been marked as special. Click here to edit the notes or change the status"
                    : "Make a note that this is a special price. This will be used when displaying the price in the future."
                }
              >
                <span
                  onClick={() => handleChangeSpecialItemNotes()}
                  css={css`
                    text-decoration: underline;
                    font-size: 12px;
                    display: inline-block;
                    color: #666;
                    cursor: pointer;
                  `}
                >
                  {data.specialItem ? "Special price*" : "Mark Special"}
                </span>
              </QuickToolTip>
              {currentUser.canEditCustomersPricing &&
                !selectedProductPriceLocked &&
                data.product &&
                quote.customer &&
                (data.price_pennies ?? 0) > 0 && (
                  <QuickToolTip content={"Manage the price bands for this customer product"}>
                    <span
                      onClick={() =>
                        handleManagePriceBands().then(() => {
                          // The bands may have changed, so don't reuse the row's pricing
                          rowPricingRef.current = null
                          loadPriceBandData()
                        })
                      }
                      css={css`
                        text-decoration: underline;
                        font-size: 12px;
                        display: inline-block;
                        color: #666;
                        cursor: pointer;
                      `}
                    >
                      Manage / Save price to band
                    </span>
                  </QuickToolTip>
                )}
            </div>
          )}

          {hideMarginColumn ? null : quote.isInternalTransfer ? null : data.product &&
            (useQuoteOrderStandardPricing
              ? data.standard_price_pennies
              : data.cost_price_pennies) ? (
            useQuoteOrderStandardPricing ? (
              <Fields.Number
                placeholder="10"
                value={
                  data.standard_discount_percent || data.standard_discount_percent === 0
                    ? String(data.standard_discount_percent)
                    : null
                }
                onChange={val => handleStandardDiscountChange(Number(val))}
                inputProps={{ min: 0 }}
                error={errors.standard_discount_percent}
                disabled={quote.isInternalTransfer}
              />
            ) : isCustomerViewing ? null : (
              <Fields.Number2
                placeholder="0"
                value={rawMargin}
                onChange={setRawMargin}
                onBlur={() => {
                  if (rawMargin === data.markUp) {
                    return
                  }
                  const margin = Math.min(Math.max(rawMargin ?? 0, 0.01), 99.99)
                  setRawMargin(margin)
                  changeMarginField(margin, handleMarkupChange)
                }}
                inputProps={{ min: 0.01, max: 99.99, step: 0.01 }}
                disabled={
                  ((usingNonStandardPrice || usingNonStandardPrice === null) && !canEditPricing) ||
                  selectedProductPriceLockedDueToSupplier ||
                  quote.isInternalTransfer
                }
                error={errors.markUp}
              />
            )
          ) : isCustomerViewing ? null : (
            <Fields.Number placeholder="" value={null} disabled />
          )}
          {/* CHILD ITEMS: Map over the child items, displaying their fields under the main item fields */}
          {childItems.length > 0 &&
            childItems.map(item => (
              <AdditionalLocationFields
                key={item.id}
                item={item}
                rawFunctions={rawFunctions}
                handleProductLocationChange={handleProductLocationChange}
                focused={focused}
                quote={quote}
                displayAllocationColumn={displayAllocationColumn}
                useExplicitAllocations={useExplicitAllocations}
              />
            ))}
          {onAppendItem && focused && canSplitStock && (
            <React.Fragment>
              <div />
              {barscanIntegrated && <div />}
              <Button
                buttonColor="white"
                onClick={() => {
                  onAppendItem({
                    product: data.product,
                    stock_code: data.stock_code,
                    body: data.body,
                    price_pennies: data.price_pennies,
                    cost_price_pennies: data.cost_price_pennies,
                    markUp: data.markUp,
                    quantity: 1,
                  })
                }}
                extendTheme={{ padding: 2 }}
              >
                Take Stock From Other Locations
              </Button>
            </React.Fragment>
          )}
        </Grid>
        {data.type === itemTypes.buildSet && (
          <div>
            <ButtonsWrapper>
              <Button
                leftIcon={icons.save}
                onClick={() => handleSaveBuildForCustomer(data.build_set_uid)}
                disabled={!quote.customer}
              >
                Save BOM
              </Button>
              <Button leftIcon={icons.plus} onClick={() => rawFunctions.handleAddBOMItemRow()}>
                Add Item
              </Button>
            </ButtonsWrapper>
            <Spacer />
          </div>
        )}
        {data.product && quote.customer && (
          <ProductInfoDisplayPanel
            productId={data.product.id}
            customerId={quote.customer.id}
            isOpen={focused}
            hideResourceId={quote.id}
            displayCurrency={quote.currency_name}
            footer={
              noProductPrice ? (
                <div
                  css={css`
                    padding-top: 8px;
                  `}
                >
                  <ProductRecommendedPriceButton
                    handleSetMargin={margin => changeMarginField(margin, handleMarkupChange)}
                    productId={data.product.id}
                    costPricePennies={data.cost_price_pennies}
                  />
                </div>
              ) : null
            }
          />
        )}
      </div>
      <RemoveRowButton
        onRemove={() => {
          if (watchedItems.length > 0) {
            watchedItems.forEach(item => {
              rawFunctions.handleRemoveItem(item)
            })
          }
          onRemove()
        }}
      />
    </React.Fragment>
  )
}

export function ProductLocationWithStock({
  data,
  childItems,
  warehouses,
  onChange,
  warehouseOverride,
}: {
  data: IItem
  childItems: IItem[]
  warehouses: IRecord<"warehouses", "name">[]
  warehouseOverride?: IRecord<"warehouses", "name">
  onChange: (item: IItem, locationId: string) => void
}) {
  // const api = useApi()
  // const [stock, setStock] = useState<Record<string, IProductLocationStockResponseItem>>()

  const locations = filterUsedLocations(
    getFilteredProductLocations(data.product?.product_locations ?? [], warehouses),
    { items: childItems },
    data.id,
  ).map(x => ({ ...x }))

  // const locationIds = locations.map((opt) => opt.id)

  // async function loadLocationStock() {
  //   const stock = await api.productLocationsStock(locationIds)
  //   setStock((old) => ({ ...old, ...stock }))
  // }
  // useAsyncEffect(loadLocationStock, [locationIds.join(",")])

  const selectedLocation = locations.find(x => x.id === data.product_location?.id)
  return (
    <Fields.Select2
      key={data.product_location?.id}
      options={locations}
      getOptionLabel={location => location.name}
      getOptionValue={location => location.id}
      formatOptionLabel={(opt, ctx) => {
        const isMenuOpen = ctx.context === "menu"
        const isSelected = (ctx.selectValue as any as { id: string }[])?.[0]?.id === opt.id
        return (
          <div>
            <P color={isMenuOpen && isSelected ? "white" : undefined} blockSpacing={0}>
              {opt.name}
              {opt.specific_location ? (
                <span
                  css={css`
                    font-size: 12px;
                    color: #999;
                  `}
                >
                  {" "}
                  ({opt.specific_location})
                </span>
              ) : (
                ""
              )}
            </P>
            {/*<P color={isMenuOpen && isSelected ? "white" : "#a1a1a1"} size="small">*/}
            {/*  In Stock: {opt.stock?.inStock ?? 0}*/}
            {/*</P>*/}
          </div>
        )
      }}
      value={selectedLocation}
      onChange={location => onChange(data, location.id ?? "")}
      disabled={!!warehouseOverride && selectedLocation?.warehouse?.id == warehouseOverride.id}
    />
  )
}

export function AlternateProductLoader({
  product,
  setHasAlternateProduct,
  handleSwitchToAlternateProduct,
}: {
  product?: { id: string; stock_code: string }
  setHasAlternateProduct: React.Dispatch<React.SetStateAction<boolean>>
  handleSwitchToAlternateProduct: (id: string) => void
}) {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const [isEOL, setIsEOL] = useState(false)
  const [alternateProduct, setAlternateProduct] = useState<IRecord<
    "products",
    "body" | "stock_code"
  > | null>(null)

  const isIreland = currentUser?.branch?.id === dublCorkBranchId

  useAsyncEffect(async () => {
    if (!product?.id) {
      setAlternateProduct(null)
    } else {
      const record = await skeem.fetch2<{
        id: string
        product_master?: {
          id: string
          end_of_life?: boolean
          product_lifecycle_status?: "End of Life"
          alternativeProductDueToDiscontinuation?: {
            name: string
            stock_code: string
            products: {
              id: string
              body: string
              stock_code: string
            }[]
          }
          alternativeProductDueToDiscontinuationIreland?: {
            name: string
            stock_code: string
            products: {
              id: string
              body: string
              stock_code: string
            }[]
          }
        }
      } | null>({
        first: {
          name: "products",
          attributes: [
            {
              name: "product_master",
              attributes: [
                "end_of_life",
                "product_lifecycle_status",
                {
                  name: "alternativeProductDueToDiscontinuation",
                  attributes: [
                    "name",
                    "stock_code",
                    {
                      name: "products",
                      attributes: ["body", "stock_code"],
                      filter: { matches: { branch: currentUser.branch.id } },
                    },
                  ],
                },
                {
                  name: "alternativeProductDueToDiscontinuationIreland",
                  attributes: [
                    "name",
                    "stock_code",
                    {
                      name: "products",
                      attributes: ["body", "stock_code"],
                      filter: { matches: { branch: currentUser.branch.id } },
                    },
                  ],
                },
              ],
            },
          ],
          filter: { matches: { id: product.id } },
        },
      })

      setIsEOL(
        !!(
          record?.product_master?.product_lifecycle_status === "End of Life" ||
          record?.product_master?.end_of_life
        ),
      )

      const alternativeSource = isIreland
        ? record?.product_master?.alternativeProductDueToDiscontinuationIreland
        : record?.product_master?.alternativeProductDueToDiscontinuation
      if (record?.product_master && alternativeSource?.products[0]) {
        setAlternateProduct(alternativeSource.products[0])
        setHasAlternateProduct(true)
      } else {
        setAlternateProduct(null)
        setHasAlternateProduct(false)
      }
    }
  }, [product?.id, isIreland])

  if (isEOL && !alternateProduct) {
    return (
      <P
        css={css`
          padding: 8px;
          background-color: #f5f5f5;
          border-radius: 8px;
        `}
      >
        <H3 color="warning">This product has been discontinued</H3>
        <P>There is no recommended alternative</P>
      </P>
    )
  }

  if (!alternateProduct || !product?.id) {
    return null
  }
  return (
    <P
      css={css`
        padding: 8px;
        background-color: #f5f5f5;
        border-radius: 8px;
      `}
    >
      <H3 color="warning">This product has been discontinued</H3>
      <P>The following product has been marked as the recommended alternative:</P>
      <P>
        <b>
          {alternateProduct.body} ({alternateProduct.stock_code})
        </b>
      </P>
      <Spacer />
      <Button
        onClick={() => handleSwitchToAlternateProduct(alternateProduct.id)}
        buttonColor="white"
      >
        Switch
      </Button>
    </P>
  )
}

/**
 * Product item additional locations fields, returns a grid formatted to render the location,
 * quantity and remove button in columns to match the main item fields
 */
function AdditionalLocationFields({
  item,
  handleProductLocationChange,
  rawFunctions,
  focused,
  quote,
  displayAllocationColumn,
}: {
  item: IItem
  handleProductLocationChange: (item: IItem, locationId: string) => void
  rawFunctions: IRowProps["rawFunctions"]
  focused?: boolean
  quote: IQuoteOrderRecord
  displayAllocationColumn?: boolean
  useExplicitAllocations?: boolean
}) {
  const associatedItems = filterDeleted(quote.items ?? []).filter(
    data => itemParentUid(data) === itemParentUid(item) && data.id !== item.id,
  )
  const { barScanOrders, warehouses, showDiscountOption } = useSettings()
  const barscanIntegrated = barScanOrders && !quote.warehouseOverride?.disable_barscan

  return (
    <React.Fragment key={item.id}>
      <div />
      {barscanIntegrated && <div />}
      <div>
        <Fields.Select
          options={
            filterUsedLocations(
              getFilteredProductLocations(item.product?.product_locations ?? [], warehouses),
              { items: associatedItems },
              item.id,
            ).map(location => ({
              label: location.name,
              value: location.id,
            })) ?? []
          }
          value={item.product_location?.id}
          onChange={location => handleProductLocationChange(item, location)}
        />
        {focused && (
          <React.Fragment>
            <Spacer size={4} />
            <StockDisplay
              data={item}
              quote={quote}
              productId={item.product?.id}
              productLocationId={item.product_location?.id}
              quoteId={quote.id}
              quantityAmount={item.quantity ?? 0}
              size="small"
            />
            <Spacer size={4} />
          </React.Fragment>
        )}
      </div>
      {showDiscountOption && <div />}
      <div>
        <Fields.Number
          placeholder="99"
          value={item.quantity || item.quantity === 0 ? String(item.quantity) : null}
          onChange={val =>
            rawFunctions.handleItemChange(item.id, oldItem =>
              displayAllocationColumn
                ? {
                    ...oldItem,
                    quantity: Number(val),
                  }
                : {
                    ...oldItem,
                    quantity: Number(val),
                    allocated_quantity: Number(val),
                  },
            )
          }
          inputProps={{ min: 1 }}
          error={rawFunctions.getErrorsForItem(item).quantity}
        />
      </div>
      {displayAllocationColumn && (
        <Fields.Number
          placeholder="99"
          value={
            item.allocated_quantity || item.allocated_quantity === 0
              ? String(item.allocated_quantity)
              : null
          }
          onChange={val =>
            rawFunctions.handleItemChange(item.id, oldItem => ({
              ...oldItem,
              allocated_quantity: Number(val),
            }))
          }
          inputProps={{ min: 1 }}
          error={rawFunctions.getErrorsForItem(item).allocated_quantity}
        />
      )}
      <div>
        {focused && (
          <QuickToolTip content={"Remove location"}>
            <IconButton
              icon={icons.cross}
              iconSize={14}
              buttonColor={brandColors.blaze}
              onClick={() => rawFunctions.handleRemoveItem(item, { skipRemoveChildren: true })}
              square
            />
          </QuickToolTip>
        )}
      </div>
      <div />
      <div />
    </React.Fragment>
  )
}

interface IStockDisplay {
  quoteId?: string
  productId?: string
  quantityAmount?: number
  productLocationId: string
  showAllBranchesLink?: boolean
  onAppendItem?(data: {
    updateQuantity: number
    newItemQuantity: number
    newProductLocation: IRecord<
      "product_locations",
      "name" | "free_stock" | "warehouse" | "specific_location"
    >
  }): void
}

export function StockDisplay({
  quoteId,
  productId,
  quantityAmount,
  productLocationId,
  showAllBranchesLink,
}: IStockDisplay) {
  const otherBranchStockModal = useOtherBranchStockModal(productId)

  const showAllBranch =
    showAllBranchesLink && productId ? (
      <P>
        <a
          css={css`
            cursor: pointer;
            transition: 0.3s;
          `}
          onClick={() => otherBranchStockModal.toggleModal()}
        >
          See stock from other branches
        </a>
        <Spacer size={4} />
      </P>
    ) : null

  return (
    <div>
      {otherBranchStockModal.node}
      {showAllBranch}
      <ProductLocationStockDisplay
        productLocationId={productLocationId}
        additionalAllocatedStock={quantityAmount ?? 0}
        orderIdForAllocationCheck={quoteId}
        unStyled
      />
    </div>
  )
}

export function ProductSelect(props: IComponentProps<typeof ExtProductSelect>) {
  return <ExtProductSelect {...props} />
}

export const productAttrsForCalculateCostPriceFromCurrency = [
  "stock_code",
  "cost_price_pennies",
  "preferred_supplier_list_price_pennies",
  { name: "preferred_supplier", attributes: ["currency_name"] },
  { name: "suppliers", attributes: ["currency_name"] },
  { name: "product_group", attributes: ["products_price_method"] },
  { name: "branch", attributes: ["currency_name"] },
]
export type IProductForCalculateCostPriceFromCurrency = IRecord<
  "products",
  | "stock_code"
  | "body"
  | "cost_price_pennies"
  | "sage_id"
  | "active"
  | "preferred_supplier_list_price_pennies"
> & {
  branch: IRecord<"branches", "currency_name">
  preferred_supplier?: IRecord<"suppliers", "currency_name">
  suppliers: IRecord<"suppliers", "currency_name">[]
  product_group?: IRecord<"product_groups", "products_price_method">
}

export function calculateCostPriceFromCurrency({
  currency_name,
  groupPrice,
  product,
  currentUser,
  priceType,
}: {
  currency_name?: string
  groupPrice?: IGroupPrice
  product: IProductForCalculateCostPriceFromCurrency
  currentUser: ReturnType<typeof useGlobalState>["currentUser"]
  priceType?: "costPrice" | "listPrice"
}) {
  // GROUP PRICING
  const priceAttr =
    priceType == "listPrice"
      ? ("preferred_supplier_list_price_pennies" as const)
      : ("cost_price_pennies" as const)
  const sourceCurrencyName = product.branch?.currency_name
  const euroToPound = currentUser.branch.euro_pound_exchange_rate ?? 1

  // setup a base price, so that don't have to do this check everywhere product.cost_price_pennies ?? 0 was done
  const basePrice = groupPrice?.[priceAttr] ?? product[priceAttr] ?? 0

  if (!sourceCurrencyName || !currency_name) {
    console.log(
      `Skipped currency check for ${
        product.stock_code ?? ""
      }: Missing currency names, source is ${sourceCurrencyName}, target is ${currency_name}`,
    )
    return basePrice
  }

  if (!basePrice) {
    return 0
  }

  return convertPriceCurrency(basePrice, euroToPound, sourceCurrencyName, currency_name)
}

export function usePriceDiffAlert({
  productId,
  renderModal,
}: {
  productId?: string
  renderModal: (data?: {
    Component: (item: { onResolve: (data: any) => any }) => any
  }) => Promise<any>
}) {
  const skeem = window.skeem
  const [id, setId] = useState(productId)

  useAsyncEffect(async () => {
    if (productId !== id) {
      setId(productId)
      await loadProduct(productId)
    }
  }, [productId])

  type ILoadedProduct = IRecord<
    "products",
    "average_buying_price_pennies" | "preferred_supplier_list_price_pennies"
  > & { branch: IRecord<"branches"> & { settings: IBranchSettings } }

  async function loadProduct(id?: string) {
    if (!id) {
      console.log("[PDA]: No product id")
      return
    }
    const product = await skeem.fetch2<ILoadedProduct>({
      first: {
        name: "products",
        attributes: [
          "average_buying_price_pennies",
          "preferred_supplier_list_price_pennies",
          { name: "branch", attributes: ["settings"] },
        ],
        matches: { id },
      },
    })
    if (!product) {
      console.log("[PDA]: No product")
      return
    }
    if (!product.branch.settings.alertPctDiff) {
      return
    }
    if (
      product.average_buying_price_pennies == null ||
      product.average_buying_price_pennies === 0
    ) {
      console.log("[PDA]: No average price")
      return
    }
    if (
      product.preferred_supplier_list_price_pennies == null ||
      product.preferred_supplier_list_price_pennies === 0
    ) {
      console.log("[PDA]: No list price")
      return
    }

    const priceDiff =
      product.average_buying_price_pennies - product.preferred_supplier_list_price_pennies
    const priceDiffPercent = (priceDiff / product.preferred_supplier_list_price_pennies) * 100
    console.log("[PDA]: price diff", priceDiff, priceDiffPercent, {
      avg: product.average_buying_price_pennies,
      list: product.preferred_supplier_list_price_pennies,
    })
    if (priceDiffPercent > 7) {
      await renderModal({
        Component: ({ onResolve }) => (
          <Modal
            isOpen={true}
            onRequestClose={() => onResolve(null)}
            width={480}
            render={() => (
              <div>
                <TextGroupMedium
                  heading={`Product average price is ${
                    priceDiff > 0 ? "higher" : "lower"
                  } than the list price`}
                  body={
                    <React.Fragment>
                      <Spacer />
                      <P>
                        This products average price is {Math.abs(priceDiffPercent).toFixed(2)}%{" "}
                        {priceDiff > 0 ? "higher" : "lower"} than the list price.
                      </P>
                    </React.Fragment>
                  }
                ></TextGroupMedium>
                <Spacer />
                <Button buttonColor="white" onClick={() => onResolve(null)}>
                  OK
                </Button>
              </div>
            )}
          />
        ),
      })
    } else {
      console.log("[PDA]: price is within 7%", priceDiff, priceDiffPercent)
    }
  }

  return null
}
